<template>
    <div
        class="system-alert"
        :class="containerClasses"
        :data-severity="severity"
        data-test="system-alert"
        @click="handleParentElementClick"
    >
        <Message :severity="severity" size="small" :pt="messagePassThrough">
            <div v-if="primary && (totalCount > 1 || $slots.prepend)" class="system-alert-prefix">
                <slot name="prepend">
                    <PrimeButton
                        type="button"
                        variant="text"
                        size="small"
                        class="system-alert-stack-toggle"
                        data-test="system-alert-stack-toggle"
                        :label="stackLabel"
                        :icon="showingAll ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                        icon-pos="right"
                        :aria-label="stackToggleLabel"
                        :aria-expanded="showingAll ? 'true' : 'false'"
                        @click.stop="$emit('toggle')"
                    />
                </slot>
            </div>
            <div class="system-alert-main">
                <slot :alert="alert">
                    <slot name="icon">
                        <i
                            :class="['pi', severityIcon, 'system-alert-icon']"
                            aria-hidden="true"
                            data-test="system-alert-icon"
                        />
                    </slot>
                    <slot name="title">
                        <span v-if="titleText" class="system-alert-title-text">{{ titleText }}</span>
                    </slot>
                    <slot name="caption">
                        <span v-if="captionText" class="system-alert-caption-text">
                            {{ captionText }}
                        </span>
                    </slot>
                    <slot name="action">
                        <PrimeButton
                            v-if="actionLabel"
                            type="button"
                            variant="link"
                            size="small"
                            class="system-alert-action"
                            :class="{ 'tw:sr-only tw:focus-visible:not-sr-only': $isMobile }"
                            data-test="system-alert-action"
                            :label="actionLabel"
                            @click.stop="clicked"
                        />
                    </slot>
                </slot>
            </div>
            <div v-if="dismissError || alert.dismissable || $slots.close" class="system-alert-close">
                <slot name="close">
                    <span
                        v-if="dismissError"
                        class="system-alert-dismiss-error"
                        role="alert"
                        data-test="system-alert-dismiss-error"
                        @click.stop
                    >
                        {{ dismissError }}
                    </span>
                    <PrimeButton
                        v-if="alert.dismissable"
                        type="button"
                        variant="text"
                        size="small"
                        icon="pi pi-times"
                        aria-label="Dismiss alert"
                        class="system-alert-dismiss"
                        data-test="system-alert-dismiss"
                        @click.stop="$emit('dismissed', alert)"
                    />
                </slot>
            </div>
        </Message>
    </div>
</template>

<script>
import PrimeButton from 'primevue/button';
import Message from 'primevue/message';

/** Stored colours the banner supports; anything else falls back to danger so the banner stays prominent. */
export const SYSTEM_ALERT_COLORS = Object.freeze(['primary', 'link', 'info', 'success', 'warning', 'danger']);

export const DEFAULT_SYSTEM_ALERT_COLOR = 'danger';

/**
 * PrimeVue Message severity for each stored colour.
 *
 * @type {Readonly<Record<string, 'info'|'success'|'warn'|'error'>>}
 */
export const SYSTEM_ALERT_SEVERITIES = Object.freeze({
    primary: 'info',
    link: 'info',
    info: 'info',
    success: 'success',
    warning: 'warn',
    danger: 'error',
});

/**
 * PrimeIcons glyph shown beside the title for each Message severity.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const SYSTEM_ALERT_SEVERITY_ICONS = Object.freeze({
    info: 'pi-info-circle',
    success: 'pi-check-circle',
    warn: 'pi-exclamation-triangle',
    error: 'pi-exclamation-circle',
});

/**
 * Map a stored alert colour to its Message severity. Unknown colours use the danger severity.
 *
 * @param {unknown} color
 * @returns {'info'|'success'|'warn'|'error'}
 */
export function systemAlertSeverity(color) {
    const supported = SYSTEM_ALERT_COLORS.includes(color) ? color : DEFAULT_SYSTEM_ALERT_COLOR;

    return SYSTEM_ALERT_SEVERITIES[supported];
}

/**
 * Admin-authored title and caption may contain markup. Parse it in an inert document
 * (scripts never run and resources never load there) and keep only the readable text.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function systemAlertText(value) {
    if (value === null || value === undefined) {
        return '';
    }

    const source = String(value);

    if (!/[<&]/.test(source)) {
        return source.trim();
    }

    const parsed = new DOMParser().parseFromString(source, 'text/html');

    parsed.querySelectorAll('script, style, template, noscript').forEach(node => node.remove());

    return (parsed.body?.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Resolve the string the banner would navigate to, or null when it must not be followed.
 * Only same-site relative paths and https URLs qualify; javascript:, data: and other schemes
 * are refused even if one reaches the browser. The returned string is exactly the one that
 * was validated, so the check and the navigation can never see different values.
 *
 * @param {unknown} path
 * @returns {string|null}
 */
export function resolveSystemAlertPath(path) {
    if (typeof path !== 'string') {
        return null;
    }

    // The URL parser itself drops leading/trailing C0 controls and spaces.
    const candidate = path.replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '');

    // Find a scheme even if it is hidden behind any whitespace or invisible character
    // (NBSP, BOM, line separators, zero-width), which is stricter than any browser.
    const probe = candidate.replace(/[\s\u0000-\u001f\u007f-\u009f\u200b-\u200d\u2060\ufeff]/gu, '').toLowerCase();

    if (probe === '' || /^[\\/]{2}/.test(probe)) {
        return null;
    }

    const scheme = probe.match(/^([a-z][a-z0-9+.-]*):/)?.[1];

    return scheme === undefined || scheme === 'https' ? candidate : null;
}

export default {
    name: 'SystemAlert',

    components: { Message, PrimeButton },

    props: {
        alert: {
            type: Object,
            required: true,
            validator: value => {
                return value && typeof value === 'object' && 'id' in value && 'color' in value;
            },
        },

        primary: {
            type: Boolean,
            required: false,
            default: false,
        },

        showingAll: {
            type: Boolean,
            required: false,
            default: false,
        },

        totalCount: {
            type: Number,
            required: false,
            default: 1,
        },

        dismissError: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['toggle', 'dismissed'],

    computed: {
        severity() {
            return systemAlertSeverity(this.alert.color);
        },

        severityIcon() {
            return SYSTEM_ALERT_SEVERITY_ICONS[this.severity];
        },

        titleText() {
            return systemAlertText(this.alert.title);
        },

        captionText() {
            return systemAlertText(this.alert.caption);
        },

        actionPath() {
            return resolveSystemAlertPath(this.alert.action_path);
        },

        hasBody() {
            if (typeof this.alert.has_body === 'boolean') {
                return this.alert.has_body;
            }

            return systemAlertText(this.alert.body) !== '';
        },

        /** D4: an alert with a body and no link opens its detail page. */
        opensDetail() {
            return !this.alert.action_path && this.hasBody && !!this.alert.uuid;
        },

        hasDestination() {
            return !!this.actionPath || this.opensDetail;
        },

        /** A validated link reads "Open link"; a body-only alert reads "View details". */
        actionLabel() {
            if (this.actionPath) {
                return 'Open link';
            }

            return this.opensDetail ? 'View details' : null;
        },

        stackLabel() {
            return `1 of ${this.totalCount}`;
        },

        /** The accessible name starts with the visible "1 of N" text (WCAG 2.5.3 label in name). */
        stackToggleLabel() {
            return `${this.stackLabel} alerts, ${this.showingAll ? 'show fewer' : 'show all'}`;
        },

        containerClasses() {
            return {
                'system-alert--actionable': this.hasDestination,
                'system-alert--mobile': !!this.$isMobile,
            };
        },

        /**
         * Emergency (error) banners interrupt as an assertive alert so a realtime push is announced;
         * the rest are polite status messages.
         *
         * @returns {{ role: string, 'aria-live': string }}
         */
        liveRegion() {
            return this.severity === 'error'
                ? { role: 'alert', 'aria-live': 'assertive' }
                : { role: 'status', 'aria-live': 'polite' };
        },

        /** Full-width, square, compact Message: the banner sits flush at the top of every page. */
        messagePassThrough() {
            return {
                root: { class: 'system-alert-message', ...this.liveRegion },
                content: { class: 'system-alert-message-content' },
                text: { class: 'system-alert-row' },
            };
        },
    },

    methods: {
        clicked() {
            const actionPath = resolveSystemAlertPath(this.alert.action_path);

            if (actionPath) {
                window.location.href = actionPath;

                return;
            }

            if (!this.opensDetail) {
                return;
            }

            const detail = { name: 'system-alerts', params: { systemAlertUuid: this.alert.uuid } };

            if (this.$router) {
                this.$router.push(detail).catch(() => {});

                return;
            }

            window.location.href = `/system-alerts/${encodeURIComponent(this.alert.uuid)}`;
        },

        handleParentElementClick() {
            this.clicked();
        },
    },
};
</script>

<style lang="scss" scoped>
/*
 * The Message supplies the severity tint, text colour and type scale (size="small" is the 14px step).
 * These unlayered rules only reshape it into a full-width strip; PrimeVue's own styles live in a
 * cascade layer, so utilities passed through `pt` cannot override them reliably.
 */
.system-alert {
    --system-alert-border-color: var(--p-message-error-border-color);
    --system-alert-light-text-color: var(--p-red-700);

    position: relative;
    z-index: 60;
    width: 100%;
    min-width: 0;
}

.system-alert[data-severity='info'] {
    --system-alert-border-color: var(--p-message-info-border-color);
    --system-alert-light-text-color: var(--p-blue-700);
}

.system-alert[data-severity='success'] {
    --system-alert-border-color: var(--p-message-success-border-color);
    --system-alert-light-text-color: var(--p-green-800);
}

.system-alert[data-severity='warn'] {
    --system-alert-border-color: var(--p-message-warn-border-color);
    --system-alert-light-text-color: var(--p-yellow-800);
}

/*
 * Aura's light-mode Message text is too light on the tinted strip for 14px text (warn 2.84:1,
 * success 3.15:1, error 4.42:1). Darker shades keep every severity near 6:1 (WCAG AA is 4.5:1);
 * the icon, action and close button inherit it. Dark mode keeps the theme's colours.
 */
html:not(.app-dark) .system-alert :deep(.system-alert-message) {
    color: var(--system-alert-light-text-color);
}

.system-alert--actionable {
    cursor: pointer;
}

/* Message is a grid; a minmax(0, 1fr) track lets nowrap text shrink and truncate instead of widening the row. */
.system-alert :deep(.system-alert-message) {
    grid-template-columns: minmax(0, 1fr);
    border-radius: 0;
    outline: none;
    box-shadow: inset 0 -1px 0 var(--system-alert-border-color);
}

.system-alert :deep(.system-alert-message-content) {
    min-width: 0;
    min-height: 44px;
    padding: 0 0.5rem 0 1rem;
}

/* The Message's text row and the title/caption group inside it lay out the same way. */
.system-alert :deep(.system-alert-row),
.system-alert-main {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: 0.625rem;
    min-width: 0;
    overflow: hidden;
    font-weight: 400;
    white-space: nowrap;
}

.system-alert-prefix,
.system-alert-close {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.25rem;
}

.system-alert-prefix {
    padding-right: 0.5rem;
    border-right: 1px solid currentColor;
}

.system-alert-icon {
    flex: 0 0 auto;
    font-size: 1.125rem;
}

.system-alert-title-text,
.system-alert-caption-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.system-alert-title-text {
    flex: 0 1 auto;
    font-weight: 700;
}

.system-alert-caption-text {
    flex: 0 1 auto;
}

.system-alert-title-text + .system-alert-caption-text {
    flex-shrink: 10;
}

/* Buttons take the Message's colour rather than the primary colour. */
.system-alert :deep(.p-button) {
    flex: 0 0 auto;
    color: inherit;
    font-size: inherit;
}

.system-alert :deep(.p-button:not(:disabled):hover) {
    color: inherit;
    background: color-mix(in srgb, currentColor 12%, transparent);
}

.system-alert :deep(.system-alert-stack-toggle .p-button-label),
.system-alert :deep(.system-alert-action .p-button-label) {
    font-weight: 700;
}

.system-alert :deep(.system-alert-stack-toggle) {
    padding: 0.25rem 0.375rem;
}

.system-alert :deep(.system-alert-action) {
    margin-inline-start: auto;
    padding: 0.25rem 0.375rem;
    text-decoration: underline;
}

.system-alert :deep(.system-alert-action:not(:disabled):hover) {
    background: transparent;
}

.system-alert :deep(.system-alert-dismiss) {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
}

/* Phones: the whole banner is the tap target (the action shows only on keyboard focus); tighter spacing so the title fits. */

/* With a caption, the title keeps at most half the row so the caption still shows after it. */
.system-alert--mobile .system-alert-title-text:has(+ .system-alert-caption-text) {
    max-width: 50%;
}

.system-alert--mobile :deep(.system-alert-message-content) {
    padding: 0 0.25rem 0 0.75rem;
}

.system-alert--mobile :deep(.system-alert-row),
.system-alert--mobile .system-alert-main {
    gap: 0.5rem;
}

.system-alert--mobile .system-alert-prefix {
    padding-right: 0.375rem;
}

.system-alert--mobile :deep(.system-alert-stack-toggle) {
    gap: 0.25rem;
    padding: 0.25rem;
}

.system-alert--mobile :deep(.system-alert-dismiss) {
    width: 2rem;
    height: 2rem;
}

.system-alert-dismiss-error {
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
}
</style>
